import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { logoutUser, getCurrentUserData } from '../services/authService';
import { isAdmin } from '../services/adminService';
import UserSettingsModal from './UserSettingsModal';
import PropTypes from 'prop-types';

function Header({ user, onShowTutorial }) {
  const navigate = useNavigate();
  const [showSettings, setShowSettings] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [userData, setUserData] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const fetchUserData = async () => {
      if (user) {
        console.log('Header: Fetching user data for', user.uid);
        const result = await getCurrentUserData(user.uid);
        console.log('Header: User data result:', result);
        if (result.success) {
          setUserData(result.data);
          console.log('Header: User data set:', result.data);
          console.log('Header: Is admin?', isAdmin(result.data));
        }
      }
    };

    fetchUserData();
  }, [user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  const handleLogout = async () => {
    const result = await logoutUser();
    if (result.success) {
      navigate('/');
    }
  };

  return (
    <header className="bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg">
      <div className="container mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          <Link
            to="/dashboard"
            className="flex items-center space-x-3 hover:opacity-90 transition"
          >
            <img
              src="/travaili-logo.png"
              alt="Travaili"
              className="h-12 w-12 object-contain"
            />
            <h1 className="text-2xl font-bold">
              TRAV
              <span className="text-orange-400 italic font-extrabold transform inline-block" style={{ transform: 'skew(-10deg)' }}>
                AI
              </span>
              LI
            </h1>
          </Link>

          <div className="flex items-center space-x-4">
            {/* Admin Link */}
            {userData && isAdmin(userData) && (
              <Link
                to="/admin"
                className="flex items-center space-x-2 hover:bg-blue-700 px-3 py-2 rounded-lg transition"
                title="Admin Dashboard"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span className="font-semibold">Admin</span>
              </Link>
            )}

            {/* User Menu Dropdown */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-2 hover:bg-blue-700 px-3 py-2 rounded-lg transition"
                title="User Menu"
              >
                <i className="fas fa-user-circle text-2xl"></i>
                <span className="font-semibold">
                  {user?.displayName || user?.email}
                </span>
                <i className={`fas fa-chevron-down text-sm transition-transform ${showUserMenu ? 'rotate-180' : ''}`}></i>
              </button>

              {/* Dropdown Menu */}
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg overflow-hidden z-50">
                  <div className="py-1">
                    <button
                      onClick={() => {
                        setShowSettings(true);
                        setShowUserMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 text-gray-700 hover:bg-blue-50 transition flex items-center space-x-3"
                    >
                      <i className="fas fa-cog text-blue-600 w-5"></i>
                      <span className="font-medium">Settings</span>
                    </button>

                    <button
                      onClick={() => {
                        if (onShowTutorial) {
                          onShowTutorial();
                        }
                        setShowUserMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 text-gray-700 hover:bg-blue-50 transition flex items-center space-x-3"
                    >
                      <i className="fas fa-play-circle text-blue-600 w-5"></i>
                      <span className="font-medium">Tutorial Video</span>
                    </button>

                    <div className="border-t border-gray-200"></div>

                    <a
                      href="/privacy-policy.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full text-left px-4 py-3 text-gray-700 hover:bg-blue-50 transition flex items-center space-x-3"
                    >
                      <i className="fas fa-shield-alt text-blue-600 w-5"></i>
                      <span className="font-medium">Privacy Policy</span>
                    </a>

                    <a
                      href="/terms-of-service.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full text-left px-4 py-3 text-gray-700 hover:bg-blue-50 transition flex items-center space-x-3"
                    >
                      <i className="fas fa-file-contract text-blue-600 w-5"></i>
                      <span className="font-medium">Terms of Service</span>
                    </a>

                    <div className="border-t border-gray-200"></div>

                    <button
                      onClick={() => {
                        handleLogout();
                        setShowUserMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 text-red-600 hover:bg-red-50 transition flex items-center space-x-3"
                    >
                      <i className="fas fa-sign-out-alt w-5"></i>
                      <span className="font-medium">Logout</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* User Settings Modal */}
      {showSettings && (
        <UserSettingsModal
          user={user}
          onClose={() => setShowSettings(false)}
        />
      )}
    </header>
  );
}

Header.propTypes = {
  user: PropTypes.object.isRequired,
  onShowTutorial: PropTypes.func,
};

export default Header;
